import React, { useState, useEffect } from "react";
import { Link, NavLink, useNavigate, Outlet } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { api } from "@/lib/api";
import {
  LayoutDashboard,
  Users,
  FileText,
  Wallet,
  Settings,
  LogOut,
  FileSignature,
  UserCog,
  Menu,
  X,
  ChevronsLeft,
  ChevronsRight,
  Search,
  Plus,
  RefreshCw,
} from "lucide-react";
import { toast } from "sonner";
import AgentChat from "@/components/AgentChat";

const navItems = [
  { to: "/", label: "Home", icon: LayoutDashboard, testId: "nav-dashboard" },
  { to: "/contracts", label: "Contracts", icon: FileSignature, testId: "nav-contracts" },
  { to: "/customers", label: "Customers", icon: Users, testId: "nav-customers" },
  { to: "/invoices", label: "Invoices", icon: FileText, testId: "nav-invoices" },
  { to: "/costs", label: "Costs", icon: Wallet, testId: "nav-costs" },
  { to: "/employees", label: "Employees", icon: UserCog, testId: "nav-employees" },
  { to: "/settings", label: "Settings", icon: Settings, testId: "nav-settings" },
];

const SIDEBAR_BG = "#1A2332";
const ACCENT_BLUE = "#408DFB";

const SidebarContent = ({ onLogout, onItemClick, collapsed, onToggleCollapse }) => (
  <div className="h-full flex flex-col relative" style={{ background: SIDEBAR_BG }}>
    {/* Compact top spacer with collapse toggle */}
    <div
      className="border-b flex items-center justify-center py-4"
      style={{ borderColor: "rgba(255,255,255,0.08)" }}
    >
      <span className="text-white/40 text-[10px] uppercase tracking-[0.25em] font-medium">
        {collapsed ? "" : "Workspace"}
      </span>
    </div>

    {/* Desktop collapse toggle */}
    {onToggleCollapse && (
      <button
        onClick={onToggleCollapse}
        data-testid="sidebar-collapse-toggle"
        className="hidden lg:flex absolute -right-3 top-3 z-10 w-6 h-6 items-center justify-center text-white hover:bg-white hover:text-[#1A2332] transition-colors"
        style={{ background: SIDEBAR_BG, border: "1px solid rgba(255,255,255,0.25)", borderRadius: "50%" }}
        aria-label={collapsed ? "Expand sidebar" : "Collapse sidebar"}
        title={collapsed ? "Expand sidebar" : "Collapse sidebar"}
      >
        {collapsed ? <ChevronsRight className="w-3.5 h-3.5" /> : <ChevronsLeft className="w-3.5 h-3.5" />}
      </button>
    )}

    {/* Nav */}
    <nav className={`flex-1 overflow-y-auto py-3 space-y-0.5 ${collapsed ? "px-2" : "px-3"}`}>
      {navItems.map((it) => (
        <NavLink
          key={it.to}
          to={it.to}
          end={it.to === "/"}
          onClick={onItemClick}
          data-testid={it.testId}
          title={collapsed ? it.label : undefined}
          className={({ isActive }) =>
            `flex items-center text-sm font-medium transition-all rounded ${
              collapsed ? "justify-center px-2 py-2.5" : "gap-3 px-3 py-2.5"
            } ${
              isActive
                ? "bg-white text-[#1A2332] shadow-sm"
                : "text-white/85 hover:bg-white/10 hover:text-white"
            }`
          }
        >
          <it.icon className="w-[18px] h-[18px] shrink-0" strokeWidth={1.8} />
          {!collapsed && <span className="truncate">{it.label}</span>}
        </NavLink>
      ))}
    </nav>

    {/* Bottom logout */}
    <div
      className={`border-t ${collapsed ? "p-2" : "p-3"}`}
      style={{ borderColor: "rgba(255,255,255,0.08)" }}
    >
      <button
        onClick={onLogout}
        data-testid="logout-button"
        title={collapsed ? "Sign out" : undefined}
        className={`w-full text-sm font-medium text-white/85 hover:bg-white/10 hover:text-white transition-colors flex items-center rounded ${
          collapsed ? "justify-center px-2 py-2.5" : "gap-3 px-3 py-2.5"
        }`}
      >
        <LogOut className="w-[18px] h-[18px] shrink-0" strokeWidth={1.8} />
        {!collapsed && "Sign Out"}
      </button>
    </div>
  </div>
);

const TopBar = ({ email, onToggleMobile }) => {
  const [syncing, setSyncing] = useState(false);
  const [userMenu, setUserMenu] = useState(false);
  const navigate = useNavigate();
  const { logout } = useAuth();

  const handleSync = async () => {
    setSyncing(true);
    try {
      const r = await api.post("/zoho/sync");
      toast.success(`Synced ${r.data.counts.invoices} invoices · ${r.data.counts.salesorders} POs`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Sync failed (check Zoho connection)");
    } finally {
      setSyncing(false);
    }
  };

  const displayName = (email || "").split("@")[0] || "User";

  return (
    <header
      data-testid="app-topbar"
      className="sticky top-0 z-30 bg-white border-b flex items-center justify-between px-3 lg:px-5 h-16 gap-3"
      style={{ borderColor: "#E5E7EB" }}
    >
      {/* LEFT: mobile menu + logos (like landing page) + search */}
      <div className="flex items-center gap-3 flex-1 min-w-0">
        <button
          onClick={onToggleMobile}
          data-testid="mobile-menu-toggle"
          className="lg:hidden p-1.5 rounded hover:bg-gray-100"
          aria-label="Open menu"
        >
          <Menu className="w-5 h-5 text-[#1A2332]" />
        </button>

        {/* Brand logos (matches landing page styling) */}
        <Link to="/" className="flex items-center gap-2 shrink-0" data-testid="topbar-brand-link">
          <div className="bg-white border border-gray-200 p-1 flex items-center justify-center" style={{ width: 42, height: 42, borderRadius: 6 }}>
            <img
              src="/logos/vaku.png"
              alt="Vaku"
              className="w-full h-full object-contain"
              data-testid="topbar-vaku-logo"
            />
          </div>
          <div className="bg-white border border-gray-200 px-2 py-1 hidden sm:flex items-center justify-center" style={{ height: 42, borderRadius: 6 }}>
            <img
              src="/logos/digitrans.png"
              alt="DigiTrans"
              className="h-full object-contain"
              data-testid="topbar-digitrans-logo"
            />
          </div>
        </Link>

        {/* Sync */}
        <button
          onClick={handleSync}
          disabled={syncing}
          data-testid="topbar-sync-button"
          className="p-2 rounded hover:bg-gray-100 transition-colors disabled:opacity-50 shrink-0"
          title="Sync from Zoho"
        >
          <RefreshCw className={`w-[18px] h-[18px] text-[#1A2332] ${syncing ? "animate-spin" : ""}`} />
        </button>

        {/* Search */}
        <div className="flex-1 max-w-xl relative hidden md:block">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <input
            type="text"
            placeholder="Search contracts, customers, invoices..."
            data-testid="topbar-search"
            className="w-full bg-gray-50 border border-gray-200 rounded-md pl-9 pr-3 py-2 text-sm focus:outline-none focus:border-[#408DFB] focus:bg-white transition-colors"
          />
        </div>
      </div>

      {/* RIGHT: company name (top-right) + quick add + user */}
      <div className="flex items-center gap-2 shrink-0">
        <div
          className="hidden md:flex flex-col items-end text-right pr-2"
          data-testid="topbar-company-name"
        >
          <div className="text-sm font-semibold text-[#1A2332] leading-tight">
            DigiTrans Technology FZE
          </div>
          <div className="text-[10px] uppercase tracking-[0.2em] text-gray-400 leading-tight mt-0.5">
            Global &amp; Vaku product
          </div>
        </div>

        <Link
          to="/contracts"
          data-testid="topbar-quick-add"
          className="p-2 rounded text-white transition-colors hover:opacity-90 shrink-0"
          style={{ background: ACCENT_BLUE }}
          title="Quick add — Contract"
        >
          <Plus className="w-[18px] h-[18px]" strokeWidth={2.2} />
        </Link>

        <div className="relative shrink-0">
          <button
            onClick={() => setUserMenu((v) => !v)}
            data-testid="topbar-user-button"
            className="w-9 h-9 rounded-full flex items-center justify-center text-white text-sm font-semibold hover:opacity-90 transition-opacity"
            style={{ background: ACCENT_BLUE }}
            title={email}
          >
            {(displayName[0] || "U").toUpperCase()}
          </button>
          {userMenu && (
            <>
              <div className="fixed inset-0 z-30" onClick={() => setUserMenu(false)} />
              <div className="absolute right-0 top-12 w-64 bg-white border border-gray-200 rounded-md shadow-lg z-40 overflow-hidden" data-testid="topbar-user-menu">
                <div className="px-4 py-3 border-b border-gray-100">
                  <div className="text-xs text-gray-500 uppercase tracking-wider">Signed in as</div>
                  <div className="text-sm font-medium text-[#1A2332] mt-1 truncate" data-testid="user-email">{email}</div>
                </div>
                <button
                  onClick={() => { setUserMenu(false); navigate("/settings"); }}
                  className="w-full text-left px-4 py-2.5 text-sm hover:bg-gray-50 flex items-center gap-2"
                >
                  <Settings className="w-4 h-4 text-gray-500" /> Settings
                </button>
                <button
                  onClick={() => { setUserMenu(false); logout(); navigate("/login"); }}
                  data-testid="user-menu-logout"
                  className="w-full text-left px-4 py-2.5 text-sm hover:bg-gray-50 flex items-center gap-2 text-red-600 border-t border-gray-100"
                >
                  <LogOut className="w-4 h-4" /> Sign Out
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </header>
  );
};

const AppLayout = () => {
  const { email, logout } = useAuth();
  const navigate = useNavigate();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [collapsed, setCollapsed] = useState(() => {
    try {
      return localStorage.getItem("sidebar:collapsed") === "1";
    } catch {
      return false;
    }
  });

  useEffect(() => {
    try {
      localStorage.setItem("sidebar:collapsed", collapsed ? "1" : "0");
    } catch {
      /* ignore */
    }
  }, [collapsed]);

  const handleLogout = () => {
    logout();
    navigate("/login");
  };

  const sidebarWidth = collapsed ? 72 : 220;

  return (
    <div className="min-h-screen flex" style={{ background: "#F7F8FA" }}>
      <aside
        data-testid="app-sidebar"
        data-collapsed={collapsed ? "true" : "false"}
        className="hidden lg:flex fixed inset-y-0 left-0 z-40 transition-[width] duration-200"
        style={{ width: sidebarWidth }}
      >
        <SidebarContent
          onLogout={handleLogout}
          collapsed={collapsed}
          onToggleCollapse={() => setCollapsed((c) => !c)}
        />
      </aside>

      {mobileOpen && (
        <div
          className="lg:hidden fixed inset-0 bg-black/50 z-40"
          onClick={() => setMobileOpen(false)}
          data-testid="mobile-overlay"
        />
      )}
      <aside
        data-testid="app-sidebar-mobile"
        className={`lg:hidden fixed inset-y-0 left-0 w-[220px] z-50 transform transition-transform ${
          mobileOpen ? "translate-x-0" : "-translate-x-full"
        }`}
      >
        <SidebarContent
          onLogout={handleLogout}
          onItemClick={() => setMobileOpen(false)}
        />
        <button
          onClick={() => setMobileOpen(false)}
          data-testid="mobile-menu-close"
          className="absolute top-3 right-3 text-white/70 hover:text-white p-1.5"
          aria-label="Close menu"
        >
          <X className="w-5 h-5" />
        </button>
      </aside>

      <div
        className={`flex-1 flex flex-col min-w-0 transition-[margin] duration-200 ${
          collapsed ? "lg:ml-[72px]" : "lg:ml-[220px]"
        }`}
      >
        <TopBar email={email} onToggleMobile={() => setMobileOpen(true)} />

        <main className="flex-1 min-w-0">
          <Outlet />
        </main>

        <footer className="border-t bg-white" style={{ borderColor: "#E5E7EB" }}>
          <div className="px-6 py-3 flex items-center justify-between text-xs text-gray-500">
            <span>Tracker · DigiTrans Global &amp; Vaku product · v0.1</span>
            <span>{new Date().getFullYear()}</span>
          </div>
        </footer>
      </div>

      {/* Floating AI agent */}
      <AgentChat />
    </div>
  );
};

export default AppLayout;
